var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (_) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import React from 'react';
import { BarcodeFormat, BrowserMultiFormatReader } from '@zxing/browser';
import { getQueryParamByName } from '../utils/urlDecoder';
import './BarcodeScannerPage.less';
var normalizeBarcode = function (value) { return value.replace(/\D/g, ''); };
var isValidIsbn13 = function (isbn) {
    if (!/^(978|979)\d{10}$/.test(isbn)) {
        return false;
    }
    var sum = isbn
        .slice(0, 12)
        .split('')
        .reduce(function (total, digit, index) { return total + Number(digit) * (index % 2 === 0 ? 1 : 3); }, 0);
    var checkDigit = (10 - (sum % 10)) % 10;
    return checkDigit === Number(isbn[12]);
};
export var findIsbn13InBarcode = function (value) {
    var barcode = normalizeBarcode(value);
    for (var index = 0; index <= barcode.length - 13; index += 1) {
        var candidate = barcode.slice(index, index + 13);
        if (isValidIsbn13(candidate)) {
            return candidate;
        }
    }
    return null;
};
var getInvalidBarcodeMessage = function (value) {
    var barcode = normalizeBarcode(value);
    if (barcode.length < 13) {
        return null;
    }
    if (barcode.length > 13) {
        return 'Vi fant flere tall i strekkoden, men ikke et gyldig ISBN. Prøv å holde kameraet nærmere ISBN-strekkoden.';
    }
    return 'Vi fant en strekkode, men den ser ikke ut som et ISBN.';
};
var getBarcodeTargetUrl = function (isbn) {
    var encodedIsbn = encodeURIComponent(isbn);
    if (getQueryParamByName('redirect') === 'sett_inn_annonse') {
        return "/sett_inn_annonse?isbn=" + encodedIsbn;
    }
    return "/sok?ean=" + encodedIsbn;
};
var getMediaDevices = function () {
    if (typeof navigator === 'undefined') {
        return undefined;
    }
    return navigator.mediaDevices;
};
var isInsecureContext = function () {
    if (typeof window === 'undefined') {
        return false;
    }
    return window.isSecureContext === false;
};
var getCameraErrorMessage = function (scanError) {
    var errorName = scanError instanceof Error ? scanError.name : '';
    if (errorName === 'NotAllowedError' || errorName === 'SecurityError') {
        return 'Kameraet er blokkert. Gi nettleseren kameratilgang, eller skriv inn ISBN under.';
    }
    if (errorName === 'NotFoundError' || errorName === 'DevicesNotFoundError') {
        return 'Fant ikke noe kamera. Skriv inn ISBN under i stedet.';
    }
    return 'Kunne ikke starte kameraet. Sjekk kameratilgang, eller skriv inn ISBN under.';
};
var getScannerSupportError = function () {
    if (isInsecureContext()) {
        return 'Kameraskanning krever HTTPS. Skriv inn ISBN under i stedet.';
    }
    var mediaDevices = getMediaDevices();
    if (!mediaDevices || typeof mediaDevices.getUserMedia !== 'function') {
        return 'Nettleseren støtter ikke kameraskanning. Skriv inn ISBN under i stedet.';
    }
    return null;
};
var BarcodeScannerPage = function () {
    var videoRef = React.useRef(null);
    var controlsRef = React.useRef(null);
    var handledResultRef = React.useRef(false);
    var _a = React.useState(null), error = _a[0], setError = _a[1];
    var _b = React.useState(false), isScanning = _b[0], setIsScanning = _b[1];
    var scannerSupportError = getScannerSupportError();
    var stopScanner = React.useCallback(function (updateState) {
        if (updateState === void 0) { updateState = true; }
        if (controlsRef.current) {
            controlsRef.current.stop();
            controlsRef.current = null;
        }
        if (updateState) {
            setIsScanning(false);
        }
    }, []);
    React.useEffect(function () { return function () {
        stopScanner(false);
    }; }, [stopScanner]);
    var goToSearch = React.useCallback(function (isbn) {
        stopScanner();
        window.location.href = getBarcodeTargetUrl(isbn);
    }, [stopScanner]);
    var handleDetectedBarcode = React.useCallback(function (value) {
        if (handledResultRef.current) {
            return;
        }
        var isbn = findIsbn13InBarcode(value);
        if (!isbn) {
            var invalidBarcodeMessage = getInvalidBarcodeMessage(value);
            if (invalidBarcodeMessage) {
                setError(invalidBarcodeMessage);
            }
            return;
        }
        handledResultRef.current = true;
        goToSearch(isbn);
    }, [goToSearch]);
    var startScanner = React.useCallback(function () { return __awaiter(void 0, void 0, void 0, function () {
        var supportError, reader, constraints, _a, scanError_1;
        return __generator(this, function (_b) {
            switch (_b.label) {
                case 0:
                    supportError = getScannerSupportError();
                    if (supportError) {
                        setError(supportError);
                        return [2 /*return*/];
                    }
                    if (!videoRef.current || isScanning) {
                        return [2 /*return*/];
                    }
                    setError(null);
                    setIsScanning(true);
                    handledResultRef.current = false;
                    _b.label = 1;
                case 1:
                    _b.trys.push([1, 3, , 4]);
                    reader = new BrowserMultiFormatReader(undefined, {
                        delayBetweenScanAttempts: 250,
                        delayBetweenScanSuccess: 250,
                    });
                    reader.possibleFormats = [BarcodeFormat.EAN_13];
                    constraints = {
                        audio: false,
                        video: {
                            facingMode: { ideal: 'environment' },
                        },
                    };
                    _a = controlsRef;
                    return [4 /*yield*/, reader.decodeFromConstraints(constraints, videoRef.current, function (result) {
                            if (result) {
                                handleDetectedBarcode(result.getText());
                            }
                        })];
                case 2:
                    _a.current = _b.sent();
                    return [3 /*break*/, 4];
                case 3:
                    scanError_1 = _b.sent();
                    setIsScanning(false);
                    setError(getCameraErrorMessage(scanError_1));
                    return [3 /*break*/, 4];
                case 4: return [2 /*return*/];
            }
        });
    }); }, [handleDetectedBarcode, isScanning]);
    return React.createElement("div", { className: "main-content barcode-scanner-page" },
        React.createElement("h1", null, "Scan strekkode"),
        React.createElement("p", null, "Bruk kameraet til \u00E5 lese ISBN-strekkoden p\u00E5 boken."),
        React.createElement("div", { className: "object barcode-scanner-page__scanner" },
            !scannerSupportError && React.createElement("video", { ref: videoRef, muted: true, playsInline: true, width: "640", height: "480", className: "barcode-scanner-page__video", "aria-label": "Kamerabilde for strekkodeskanning" }),
            (error || scannerSupportError) && (React.createElement("p", { role: "alert", className: "barcode-scanner-page__error" }, error || scannerSupportError)),
            React.createElement("p", { className: "barcode-scanner-page__actions" }, isScanning
                ? React.createElement("button", { type: "button", className: "plain", onClick: function () { return stopScanner(); } }, "Stopp skanning")
                : React.createElement("button", { type: "button", onClick: startScanner, disabled: !!scannerSupportError }, "Start skanning"))));
};
export default BarcodeScannerPage;
